import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as TemplatesActions from './templates.actions';
import { OrderTemplateInterfaceWithId } from '@sk/sk-interfaces';

export const TEMPLATES_FEATURE_KEY = 'templates';

export interface TemplatesState extends EntityState<OrderTemplateInterfaceWithId> {
  templatesLoaded: boolean; // has the Clients list been loaded
  templatesLoadError?: string | null; // last none error (if any)

  queryTemplateLoadError?: string | null; // last none error (if any)

  addTemplateError?: string | null; // last none error when adding a new client (if any)
  addTemplateSuccess: boolean; // last none error when adding a new client (if any)

  updateTemplateError?: string | null; // last none error when adding a new client (if any)
  updateTemplateSuccess: boolean; // last none error when adding a new client (if any)

  deleteTemplateError?: string | null; // last none error when adding a new client (if any)
  deleteTemplateSuccess: boolean; // last none error when adding a new client (if any)
}

export interface TemplatePartialState {
  readonly [TEMPLATES_FEATURE_KEY]: TemplatesState;
}

export const templatesAdapter: EntityAdapter<OrderTemplateInterfaceWithId> = createEntityAdapter<OrderTemplateInterfaceWithId>({
  selectId: selectTemplateId
});

export const initialState: TemplatesState = templatesAdapter.getInitialState({
  // set initial required properties
  templatesLoaded: false,
  addTemplateSuccess: false,
  updateTemplateSuccess: false,
  deleteTemplateSuccess: false
});


export function selectTemplateId(a: OrderTemplateInterfaceWithId): string {
  //In this case this would be optional since primary key is id
  return a.id;
}


const templatesReducer = createReducer(
  initialState,

  on(TemplatesActions.loadTemplates, (state) => {
    if (state.templatesLoadError) {
      delete state.templatesLoadError;
    }
    if (state.addTemplateError) {
      delete state.addTemplateError;
    }
    if (state.updateTemplateError) {
      delete state.updateTemplateError;
    }
    if (state.deleteTemplateError) {
      delete state.deleteTemplateError;
    }

    return ({
      ...state,
      templatesLoaded: false
    });
  }),

  on(TemplatesActions.loadTemplatesSuccess, (state, { templates }) => {
    return {
      ...state,
      templatesLoaded: true
    };
  }),

  on(TemplatesActions.loadTemplatesFailure, (state, { error }) => {
    if (error) {
      console.error(error);
    }
    return ({
      ...state,
      templatesLoadError: error
    });
  }),

  // ACTIONS INITIATED FROM THIS CLIENT
  // FIREBASE WILL AUTOMATICALLY UPDATE STATE THROUGH QUERY!!


  /**
   * Add Staff from Client
   */
  on(TemplatesActions.addTemplate, (state, { template }) => {
    if (state.addTemplateError) {
      delete state.addTemplateError;
    }
    return ({
      ...state,
      addTemplateSuccess: false
    });
  }),

  on(TemplatesActions.addTemplateError, (state, { error }) => {
    return ({
      ...state,
      addTemplateError: error.message
    });
  }),

  on(TemplatesActions.addTemplateSuccess, (state, {}) => {
    if (state.addTemplateError) {
      delete state.addTemplateError;
    }
    return ({
      ...state,
      addTemplateSuccess: true
    });
  }),

  /**
   * Delete Staff from Client
   */
  on(TemplatesActions.removeTemplate, (state, {
    id
  }) => {
    if (state.deleteTemplateError) {
      delete state.deleteTemplateError;
    }
    return templatesAdapter.removeOne(id, {
      ...state,
      deleteTemplateSuccess: false
    });
  }),


  on(TemplatesActions.removeTemplateSuccess, (state, {}) => {
    if (state.deleteTemplateError) {
      delete state.deleteTemplateError;
    }
    return ({
      ...state,
      deleteTemplateSuccess: true
    });
  }),


  on(TemplatesActions.removeTemplateError, (state, {
    error
  }) => {
    return ({
      ...state,
      deleteTemplateError: error.message
    });

  }),


  /**
   * Update staff from Client
   */
  on(TemplatesActions.updateTemplate, (state, { id, changes }) => {
    if (state.updateTemplateError) {
      delete state.updateTemplateError;
    }
    return templatesAdapter.updateOne({
        id,
        changes
      },
      {
        ...state,
        updateTemplateSuccess: false
      });
  }),

  on(TemplatesActions.updateTemplateSuccess, (state, {}) => {
    if (state.updateTemplateError) {
      delete state.updateTemplateError;
    }
    return {
      ...state,
      updateTemplateSuccess: true
    };
  }),

  on(TemplatesActions.updateTemplatesError, (state, { error }) => {
    return ({
      ...state,
      updateTemplateError: error.message
    });
  }),


  /**
   * FIRSTORE ACTIONS
   */
  on(TemplatesActions.fromFireStoreAddTemplate, (state, { template }) => {
    if (state.queryTemplateLoadError) {
      delete state.queryTemplateLoadError;
    }
    return templatesAdapter.setOne(template, state);
  }),

  on(TemplatesActions.fromFireStoreModifyTemplate, (state, { template }) => {
    if (state.queryTemplateLoadError) {
      delete state.queryTemplateLoadError;
    }
    return templatesAdapter.updateOne({
        id: template.id,
        changes: template
      },
      state);
  }),
  on(TemplatesActions.fromFireStoreRemoveTemplate, (state, {
    id
  }) => {
    if (state.queryTemplateLoadError) {
      delete state.queryTemplateLoadError;
    }
    return templatesAdapter.removeOne(id, state);
  }),
  on(TemplatesActions.queryFireStoreTemplatesError, (state, {
    error
  }) => {
    return {
      queryTemplateLoadError: error.message,
      ...state
    };

  })
);


export function reducer(state: TemplatesState | undefined, action: Action) {
  return templatesReducer(state, action);
}
